import React, { useEffect, useState } from 'react'
import { ListGroup } from "react-bootstrap"
import { Skeleton, Typography, Box, Stack, MenuItem, Select ,Button, CircularProgress } from '@mui/material'
import { SelectChangeEvent } from '@mui/material/Select'

import { get ,put } from '../../../utils/http'
import { FullNodeProp, SlideMetaData } from '../../../utils/types'
import { isPrivate } from '../../../utils/config'

interface SlideInfoProps {
    slideData: FullNodeProp
}

interface FormData {
    [key: string]: any,
    tissue_type: string,
    slide_type: string,
    tiling_status: string,
    annotation_status: string,
    tissuearea_status: string,
    bcr_status: string,
}

const SlideInfo = ({ slideData }: SlideInfoProps) => {
    const [metaData, setMetaData] = useState<SlideMetaData | null | {}>(null)
    const [formData, setFormData] = useState<FormData>({
        tissue_type: '',
        slide_type: '',
        tiling_status: '',
        annotation_status: '',
        tissuearea_status: '',
        bcr_status: '',
    })
    const [enableBtn, setEnableBtn] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(false)

    const userRole = localStorage.getItem('role')
    const isAdmin = ['admin', 'dev', 'superadmin'].includes(userRole || 'null')
    const predStatusList = ['Pending', 'Start', 'Queued', 'Processing', 'Stop', 'Processed', 'Stopped', 'Failed']
    const predStatusReadOnlyList = ['Pending', 'Queued', 'Processing', 'Stopped', 'Failed']

    const getData = async () => {
        try {
            const res = await get(`Images/metadata/${slideData.file_id}`)
            setMetaData(res)
            setFormData(res)
        } catch (err) {
            setMetaData({})
        }
    }

    useEffect(() => {
        getData()
    }, [slideData]) // eslint-disable-line

    useEffect(() => {
        if (metaData && 'id' in metaData) {
            let mismatch = false
            for (let i in formData)
                if (formData[i] !== metaData[i]) mismatch = true
            setEnableBtn(mismatch)
        }
    }, [formData])  // eslint-disable-line

    const handleInputChange = (e: SelectChangeEvent) => {
        if (e.target.name === 'tissue_type' && e.target.value !== 'prostate') {
            setFormData(prevForm => ({ ...prevForm, slide_type: '' }))
        }
        setFormData(prevForm => ({ ...prevForm, [e.target.name]: e.target.value }))
    }

    const updateSlideInfo = async () => {
        setLoading(true)
        try {
            if (metaData && 'id' in metaData) {
                const payload: any = {}
                for (let i in formData)
                    if (formData[i] !== metaData[i]) payload[i] = formData[i]
                await put(`Images/${metaData.id}`, payload)
                const temp: any = {...metaData, ...formData}
                setMetaData(temp)
                setFormData(temp)
            }
        } catch (err) {
            console.log(err)
        } finally {
            setLoading(false)
        }
    }

    return (
        <Box className='d-flex flex-column gap-4'>
            <Typography variant="h5" component="h1">File info</Typography>
            {metaData
                ? 'id' in metaData
                    ? <Box className='d-flex flex-column gap-4'>
                        <ListGroup style={{ display: 'grid', gridTemplateColumns: 'reapt(2, 1fr)', gap: '1rem' }}>
                            <ListGroup.Item className='d-flex gap-2 p-0 border-0' style={{ gridArea: '1 / 1 / span 1 / span 2' }}>
                                <Typography>Name:</Typography>
                                <Typography className="m-0 text-limit" style={{ maxWidth: 400 }} title={/[/]/ig.test(metaData.name) ? metaData.name.split('/')[metaData.name.split('/').length-1] : metaData.name}>
                                    {/[/]/ig.test(metaData.name) ? metaData.name.split('/')[metaData.name.split('/').length-1] : metaData.name}
                                </Typography>
                            </ListGroup.Item>
                            <ListGroup.Item className='d-flex gap-2 p-0 border-0' style={{ gridArea: '2 / 1 / span 1 / span 2' }}>
                                <Typography>File ID:</Typography>
                                <Typography className="m-0 text-nowrap">{metaData.key || '-'}</Typography>
                            </ListGroup.Item>
                            <ListGroup.Item className='d-flex gap-2 p-0 border-0'>
                                <Typography>Status:</Typography>
                                <Typography className="m-0 text-nowrap text-capitalize">{metaData.status}</Typography>
                            </ListGroup.Item>
                            <ListGroup.Item className='d-flex gap-2 p-0 border-0'>
                                <Typography>Upload Date:</Typography>
                                <Typography>{new Date(metaData.adddate).toISOString().split("T")[0]}</Typography>
                            </ListGroup.Item>
                            {/* <ListGroup.Item className='d-flex gap-2 p-0 border-0'>
                                <Typography>Size (MB):</Typography> 
                                <Typography className="m-0 text-nowrap">{ metaData.size || '-' }</Typography>
                            </ListGroup.Item> */}
                            <ListGroup.Item className='d-flex gap-2 p-0 border-0'>
                                <Typography>Specimen Type:</Typography>
                                <Select
                                    className='padding-fix'
                                    name='slide_type'
                                    value={formData.slide_type}
                                    onChange={handleInputChange}
                                    readOnly={metaData.status === "Delete" || !isAdmin}
                                >
                                    <MenuItem value="biopsy" disabled={formData.tissue_type !== 'prostate'}>Biopsy</MenuItem>
                                    <MenuItem value="rp" disabled={formData.tissue_type !== 'prostate'}>RP</MenuItem>
                                </Select>
                            </ListGroup.Item>
                            <ListGroup.Item className='d-flex gap-2 p-0 border-0'>
                                <Typography>Tissue Type:</Typography>
                                <Select
                                    className='padding-fix'
                                    name='tissue_type'
                                    value={formData.tissue_type}
                                    onChange={handleInputChange}
                                    readOnly={metaData.status === "Delete" || !isAdmin}
                                >
                                    <MenuItem value="prostate">Prostate</MenuItem>
                                    <MenuItem value="lung">Lung</MenuItem>
                                    <MenuItem value="breast">Breast</MenuItem>
                                </Select>
                            </ListGroup.Item>
                            <ListGroup.Item className='d-flex gap-2 p-0 border-0'>
                                <Typography>Gleason Grading:</Typography>
                                <Select
                                    className='padding-fix'
                                    name='annotation_status'
                                    value={formData.annotation_status}
                                    onChange={handleInputChange}
                                    readOnly={metaData.status === "Delete" || !isAdmin}
                                >
                                    {predStatusList.map((i, index) => <MenuItem 
                                        key={index}
                                        value={i}
                                        disabled={predStatusReadOnlyList.includes(i)}
                                        sx={{display: predStatusReadOnlyList.includes(i) ? 'none' : 'block'}}
                                    >
                                        {i}
                                    </MenuItem>)}
                                </Select>
                            </ListGroup.Item>
                            <ListGroup.Item className='d-flex gap-2 p-0 border-0'>
                                <Typography>Tiling:</Typography>
                                <Select
                                    className='padding-fix'
                                    name='tiling_status'
                                    value={formData.tiling_status}
                                    onChange={handleInputChange}
                                    readOnly={metaData.status === "Delete" || !isAdmin}
                                >
                                    {predStatusList.map((i, index) => <MenuItem 
                                        key={index}
                                        value={i}
                                        disabled={predStatusReadOnlyList.includes(i)}
                                        sx={{display: predStatusReadOnlyList.includes(i) ? 'none' : 'block'}}
                                    >
                                        {i}
                                    </MenuItem>)}
                                </Select>
                            </ListGroup.Item>
                            <ListGroup.Item className='d-flex gap-2 p-0 border-0'>
                                <Typography>Outcome Prediction:</Typography>
                                <Select
                                    className='padding-fix'
                                    name='bcr_status'
                                    value={formData.bcr_status}
                                    onChange={handleInputChange}
                                    readOnly={metaData.status === "Delete" || !isAdmin}
                                >
                                    {predStatusList.map((i, index) => <MenuItem 
                                        key={index}
                                        value={i}
                                        disabled={predStatusReadOnlyList.includes(i)}
                                        sx={{display: predStatusReadOnlyList.includes(i) ? 'none' : 'block'}}
                                    >
                                        {i}
                                    </MenuItem>)}
                                </Select>
                            </ListGroup.Item>
                            <ListGroup.Item className='d-flex gap-2 p-0 border-0'>
                                <Typography>Tissue Area:</Typography>
                                <Select
                                    className='padding-fix'
                                    name='tissuearea_status'
                                    value={formData.tissuearea_status}
                                    onChange={handleInputChange}
                                    readOnly={metaData.status === "Delete" || !isAdmin}
                                >
                                    {predStatusList.map((i, index) => <MenuItem 
                                        key={index}
                                        value={i}
                                        disabled={predStatusReadOnlyList.includes(i)}
                                        sx={{display: predStatusReadOnlyList.includes(i) ? 'none' : 'block'}}
                                    >
                                        {i}
                                    </MenuItem>)}
                                </Select>
                            </ListGroup.Item>
                        </ListGroup>
                        {(isPrivate || isAdmin) && 
                            <Box className='d-flex justify-content-center'>
                                {loading
                                    ? <CircularProgress size={30} />
                                    : <Button className='px-4' variant="contained" onClick={updateSlideInfo} disabled={!enableBtn}>Save</Button>
                                }
                            </Box>
                        }
                    </Box>
                    : <Typography>Data not found</Typography>
                : <Stack spacing={1}>
                {Array.from({length: 6}, (_, i) => i).map(i => (<Skeleton key={i} variant="rectangular" height={34} />))}
                </Stack>
            }
        </Box>
    )
}

export default SlideInfo